import React, {useState, useEffect} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  Grid,
  Typography,
  Button,
  Fab,
  Icon
} from '@material-ui/core';
import {
  getStoreLogosApi,
  uploadStoreLogoApi,
  getConfigsLogosApi,
  updateAllLogosApi
} from 'api';
import { makeStyles } from '@material-ui/core/styles';
import {Breadcrumb, BackdropCircular} from 'components';
import logo from 'components/Logo/logo-dermpro.svg';
import { uploadStoreLogoBadType, uploadFileSizeLimit, uploadImageResolutionLimit } from 'redux/actions/general';
import { showNotification } from 'redux/actions/ui';
import clsx from 'clsx';
import {fileToBase64, getMimeType} from '../../utils';


const MAX_LOGO_FILE_SIZE = 250 * 1024;
const MAX_LOGO_WIDTH = 1000;
const MAX_LOGO_HEIGHT = 500;

const SUPPORTED_FORMATS: Record<string, string> = {
  store: 'png, gif, jpg, jpeg',
  gift: 'png, gif, jpeg',
  invoice: 'png, jpeg',
  email: 'jpeg, jpg, png, gif',
  favicon: 'ico, png, gif, jpeg, jpg, apng'
};

const RECOMMENDED_SIZES: Record<string, string> = {
  store: '560×250px or higher (aspect ratio 2:1 to 5:1)',
  gift: 'minimum 200×50px, optimal 320×80px (aspect ratio 4:1)',
  invoice: 'minimum 450×150px, optimal 800×200px (aspect ratio 3:1 to 5:1)',
  email: 'minimum 320×150px, optimal 560×250px (aspect ratio 2:1 to 4:1)',
  favicon: '48×48px (aspect ratio 1:1, ico is preferred format)'
};

const getImageDimensions = (file: File): Promise<{ width: number; height: number } | null> => (
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      const image = new Image();
      image.onload = () => resolve({ width: image.width, height: image.height });
      image.onerror = () => resolve(null);
      image.src = event.target?.result as string;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  })
);

const useStyles = makeStyles(() => ({
  logoContainer: {
    maxHeight: '150px',
    minHeight: '150px'
  },
  logo: {
    width: '100%',
    maxWidth: '150px',
    maxHeight: '140px',
    margin: '10px 10px'
  },
  title: {
    textTransform: 'capitalize'
  },
  buttonContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
  },
  itemContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column'
  },
  greyBackground: {
    backgroundColor: '#f0f0f0'
  },
  helpText: {
    textAlign: 'center',
    maxWidth: '260px'
  }
}));

const General = () => {
  const dispatch = useDispatch();
  const [storeLogos, setStoreLogos] = useState([]);
  const { stores = [] } = useSelector((state: IReduxState) => state.user.profile);
  const {
    logos: {
      configData,
      store,
      gift,
      invoice,
      email,
      favicon,
      storeConfiguration = {},
      loading
    },
    loadigUpdateAllLogos
  } = useSelector((state: IReduxState) => state.general);
  const apiUrl = stores[0]?.apiUrl;
  const classes = useStyles();

  useEffect(() =>  {
    if (apiUrl) {
      getConfigsLogosApi({dispatch, apiUrl});
      getStoreLogosApi({dispatch});
    }
  }, [apiUrl]);

  useEffect(() => {
    setStoreLogos([store, gift, invoice, email, favicon]);
  }, [store, gift, invoice, email, favicon]);

  const setLogoImage = async (files, key, customPath) => {
    const file = await fileToBase64(files[0]);
    const fileExtension = files[0].name?.split('.').pop();
    const storeCode = stores[0]?.storeCode;
    const updatedFileName = `${new Date().getTime()}-${storeCode}-${key}-logo.${fileExtension}`;

    uploadStoreLogoApi({dispatch, key, logo: btoa(file), customPath, fileName: updatedFileName});

    if (customPath === 'sales/store/logo') {
      uploadStoreLogoApi({
        dispatch,
        key: 'invoice_html',
        logo: btoa(file),
        customPath: 'sales/store/logo_html',
        fileName: updatedFileName
      });
    }
  };

  const LogoInput = ({item, accept}) => (
    <input
      style={{ display: 'none' }}
      type="file"
      name={item.name}
      onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
        const {files, name} = event.target;
        const {0: file} = files;

        const fileName = event.target.value;
        const fileType = file.type;
        const fileTypeDotIndexPosition = fileName.lastIndexOf('.') + 1;
        const slicedFileTypeFromFilePath = fileName.slice(fileTypeDotIndexPosition);

        // image validation regex
        const acceptableFormats = accept.replace(/image\//g, '').replace(/, /gi, '|');
        const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

        const mimeType = await getMimeType(file);

        if (fileType !== mimeType) {
          dispatch(showNotification(
            'error',
            'Can\'t upload image. File type do not match to real file extension.'
          ));
        } else if (!hasImageFile.test(fileType) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
          dispatch(uploadStoreLogoBadType());
        } else if (file.size > MAX_LOGO_FILE_SIZE) {
          dispatch(uploadFileSizeLimit('250kB'));
        } else {
          const dimensions = await getImageDimensions(file);

          if (dimensions && (dimensions.width > MAX_LOGO_WIDTH || dimensions.height > MAX_LOGO_HEIGHT)) {
            dispatch(uploadImageResolutionLimit(`no more than ${MAX_LOGO_WIDTH}x${MAX_LOGO_HEIGHT}px`));
          } else {
            setLogoImage(files, name, item.customPath);
          }
        }
      }}
      id={item.name}
      accept={accept}
    />
  );

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          routeSegments={[
            { name: 'Settings', path: '/settings#go_live_requirements' },
            { name: 'UI Configurations', path: '/settings#UI_configurations' },
            { name: 'Store Logos' }
          ]}
        />
        <div className="mt-10 mx-10">
          <Card
            elevation={5} className={clsx(
              'p-8',
              classes.greyBackground
            )}
          >
            <Grid container spacing={5}>
              {storeLogos.map((item) => (
                <Grid item xs={12} sm={3} md={3} spacing={10} className={`${classes.itemContainer} p-6`} key={item.name}>
                  <Typography variant="h4" className={`my-4 ${classes.title}`}>{item.title}</Typography>
                  <div className={`${classes.logoContainer} flex items-center justify-center`}>
                    <img onError={(event: React.ChangeEvent<HTMLImageElement>) => event.target.src = logo} className={classes.logo} src={item.url || logo} alt={item.alt} />
                  </div>
                  <label htmlFor={item.name}>
                    {item.name === 'store' && <LogoInput item={item} accept="image/png, image/jpeg, image/jpg, image/gif" />}
                    {item.name === 'gift' && <LogoInput item={item} accept="image/png, image/jpeg, image/gif" />}
                    {item.name === 'invoice' && <LogoInput item={item} accept="image/png, image/jpeg" />}
                    {item.name === 'email' && <LogoInput item={item} accept="image/png, image/jpeg, image/jpg, image/gif" />}
                    {item.name === 'favicon' && <LogoInput item={item} accept="image/vnd.microsoft.icon, image/x-icon, image/ico, image/png, image/jpeg, image/jpg, image/gif, image/apng" />}
                    <Fab
                      color="primary"
                      size="small"
                      component="span"
                      aria-label="add"
                      variant="extended"
                    >
                      <Icon>add</Icon>Upload image
                    </Fab>
                  </label>
                  <div className={`text-13 text-muted pt-5 ${classes.helpText}`}>
                    <strong>Files supported:</strong> {SUPPORTED_FORMATS[item.name]}
                  </div>
                  <div className={`text-13 text-muted pt-1 ${classes.helpText}`}>
                    <strong>Recommended:</strong> {RECOMMENDED_SIZES[item.name]}
                  </div>
                </Grid>
              ))}
            </Grid>
          </Card>
          <div className={`${classes.buttonContainer} pt-5`}>
            <div className="text-13 text-muted">
              Max file size: 250kB. Max resolution: {MAX_LOGO_WIDTH}x{MAX_LOGO_HEIGHT}px (width x height). Please compress the image before uploading, when possible, by using image compression tools.
            </div>
            <Button
              variant="contained"
              color="primary"
              className="my-4"
              onClick={() => updateAllLogosApi({dispatch, storeConfiguration, configData})}
            >
              Save Configurations
            </Button>
          </div>
        </div>
      </div>
      <BackdropCircular loading={loading || loadigUpdateAllLogos} />
    </div>
  );
};

export default General;
